import type { ReactNode } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { splitFlag } from "@volca/ui/trades";
import type {
  ReportColumn,
  ReportRow,
  ReportValue,
} from "@/profitability/types";
import {
  isLoss,
  isWatchedCount,
  judgeRow,
  type RateJudgement,
  type RateRule,
} from "./judgement";

/**
 * A rate with the small dot that says which side of its target it is on:
 * green on the better side, red on the worse. The words ("4.7 pts under the
 * 48% target") are in its tooltip and read to a screen reader; the figure
 * itself stays ink.
 */
export const RateMark = ({
  judgement,
  children,
}: {
  judgement: RateJudgement | null;
  children: ReactNode;
}) => {
  if (!judgement) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger
        // On the figure's baseline, so it sits level with the cells beside it.
        render={<span className="inline-flex items-baseline gap-1.5" />}
      >
        <span
          aria-hidden
          className={cn("size-1.5 flex-none self-center rounded-full", {
            "bg-success": judgement.good,
            "bg-danger": !judgement.good,
          })}
        />
        {/* Wide enough for "−12.5%", so the dots line up down a column. */}
        <span className="inline-block min-w-12 text-right">{children}</span>
        <span className="sr-only">, {judgement.words}</span>
      </TooltipTrigger>
      <TooltipContent>{judgement.words}</TooltipContent>
    </Tooltip>
  );
};

/**
 * Why a row needs a look: an outline badge with a warning dot and the flag's
 * rule (its words before the colon), the whole reason in its tooltip and for
 * a screen reader. Nothing on a row that is fine.
 */
export const FlagBadge = ({ reason }: { reason: ReportValue | undefined }) => {
  if (typeof reason !== "string" || !reason.trim()) return null;
  const { rule, detail } = splitFlag(reason);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge variant="outline" size="sm" className="max-w-full gap-1.5" />
        }
      >
        <span
          aria-hidden
          className="size-1.5 flex-none rounded-full bg-warning"
        />
        <span className="min-w-0 truncate">{rule}</span>
        {detail && <span className="sr-only">: {detail}</span>}
      </TooltipTrigger>
      <TooltipContent className="max-w-72">{reason.trim()}</TooltipContent>
    </Tooltip>
  );
};

/**
 * A figure as a table shows it, coloured only where the report judges it:
 * its rate against the row's target, a result below zero in red, a count it
 * says should be low in amber when there is any. Everything else stays ink.
 */
export const JudgedValue = ({
  row,
  column,
  rule,
  children,
}: {
  row: ReportRow;
  column: ReportColumn;
  rule?: RateRule;
  children: ReactNode;
}) => {
  if (rule && column.key === rule.rate.key)
    return <RateMark judgement={judgeRow(rule, row)}>{children}</RateMark>;
  const value = row[column.key];
  const watched = isWatchedCount(column, value);
  return (
    <span
      title={watched ? "Fewer is better" : undefined}
      className={cn({
        "text-danger": isLoss(column, value),
        "text-warning": watched,
      })}
    >
      {children}
    </span>
  );
};
